/**
 * Optimizer-tab row-visibility rule for issue #100 ("Hide 'OFF'" toggle).
 *
 * --------------------------------------------------------------------------
 * WHY THIS FILE EXISTS (for reviewers asking "is this overkill?"):
 * --------------------------------------------------------------------------
 * Same reasoning as src/utility/dateCompare.js: the codebase has a documented
 * history (#73, #80, #83, #84, #85) of bugs where change-detection or
 * visibility logic drifted between production code paths and their tests.
 * The fix pattern is always the same — extract the critical rule into a pure
 * function and import it in both places so they cannot diverge.
 *
 * Stylistic concerns ("this file is too small") are explicitly secondary to
 * correctness guarantees per CLAUDE.md.
 *
 * --------------------------------------------------------------------------
 * THE RULE:
 * --------------------------------------------------------------------------
 * Returns true iff the installer's row should be hidden in the Optimizer view.
 *
 *   1. Guest installers (whose home location differs from the view being
 *      filtered, i.e. `candidate.cLocationCode !== locationFilter`) are NEVER
 *      hidden. At a non-home location they often render OFF by default but
 *      remain schedulable; hiding them would cause exactly the "where did the
 *      shared crew go?" support tickets this feature is meant to prevent.
 *
 *      IMPORTANT — why not `sharedInstallerFlag`: that flag is set whenever
 *      `Additional_Locations` exists on the candidate, which is true for
 *      plenty of HOME installers who also work elsewhere. It is too broad to
 *      use as the guest discriminator. Home location vs view location is the
 *      actual test. (Caught by the diagnostic run in #100 — every ABQ row
 *      was being saved by the old check and nothing hid.)
 *
 *   2. Otherwise, hide the row when EVERY day in weekDatesArray has at least
 *      one attribute document in scheduleArray matching:
 *        doc.attribute === true
 *        doc.installer === formattedPayrollNo
 *        doc.location === locationFilter
 *        doc.encodedSmallTitle === "OFF"
 *        datesEqual(doc.start, weekDate)
 *   3. Multi-attribute cells (e.g. "OFF + BUFFER", "OFF + OFF" from default
 *      plus date-specific overrides) count as OFF whenever OFF appears
 *      anywhere in the stack — we use .some() on the scheduleArray so any
 *      matching OFF doc is enough for that day.
 *   4. Empty cells (no attribute doc for that day) disqualify the row from
 *      being hidden — the installer is treated as available that day.
 */

import { datesEqual } from "./dateCompare";

export const OFF_ATTRIBUTE_VALUE = "OFF";

export function shouldHideInstallerRow(
  candidate,
  formattedPayrollNo,
  weekDatesArray,
  scheduleArray,
  locationFilter
) {
  if (!candidate) return false;
  // Guest installers (home ≠ current view) are never hidden. See header comment.
  if (candidate.cLocationCode !== locationFilter) return false;
  if (!Array.isArray(weekDatesArray) || weekDatesArray.length !== 7) return false;
  if (!Array.isArray(scheduleArray)) return false;

  return weekDatesArray.every((weekDate) =>
    scheduleArray.some(
      (doc) =>
        doc &&
        doc.attribute === true &&
        doc.installer === formattedPayrollNo &&
        doc.location === locationFilter &&
        doc.encodedSmallTitle === OFF_ATTRIBUTE_VALUE &&
        datesEqual(doc.start, weekDate)
    )
  );
}
